import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Role codes and their full names
type RoleCode = 'A' | 'S' | 'O' | 'M' | 'W' | 'R' | 'admin' | 'supervisor' | 'operator' | 'manager' | 'warehouse' | 'route';

// Map role codes to their full names for easier comparison
const roleMapping: Record<string, string[]> = {
  admin: ['A', 'admin'],
  supervisor: ['S', 'supervisor'],
  operator: ['O', 'operator'],
  manager: ['M', 'manager'],
  warehouse: ['W', 'warehouse'],
  route: ['R', 'route'],
};

// Check if user role matches any of the allowed roles
const hasAllowedRole = (userRole: string | undefined, allowedRoles: RoleCode[]): boolean => {
  if (!userRole) return false;

  return allowedRoles.some(allowedRole => {
    // Direct match
    if (userRole === allowedRole) return true;

    // Check if the allowed role maps to the user's role code
    for (const [, codes] of Object.entries(roleMapping)) {
      if (codes.includes(allowedRole) && codes.includes(userRole)) {
        return true;
      }
    }
    return false;
  });
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: RoleCode[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    if (!hasAllowedRole(user?.role, allowedRoles)) {
      // Redirect to appropriate dashboard based on user role
      if (user?.role === 'O' || user?.role === 'operator') {
        return <Navigate to="/operator/clock-in" replace />;
      }
      if (user?.role === 'S' || user?.role === 'supervisor') {
        return <Navigate to="/supervisor" replace />;
      }
      if (user?.role === 'M' || user?.role === 'manager') {
        return <Navigate to="/manager" replace />;
      }
      // Default redirect to login for unknown roles
      return <Navigate to="/login" replace />;
    }
  }

  // Redirect supervisors to their dashboard if they try to access root
  // Support both 'S' (database code) and 'supervisor' (full name)
  if ((user?.role === 'S' || user?.role === 'supervisor') && location.pathname === '/') {
    return <Navigate to="/supervisor" replace />;
  }

  // Redirect managers to their dashboard if they try to access root
  // Support both 'M' (database code) and 'manager' (full name)
  if ((user?.role === 'M' || user?.role === 'manager') && location.pathname === '/') {
    return <Navigate to="/manager/dashboard" replace />;
  }

  // Redirect admins to admin dashboard if they try to access supervisor routes
  // Support both 'A' (database code) and 'admin' (full name)
  if ((user?.role === 'A' || user?.role === 'admin') && location.pathname === '/supervisor') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;